import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  Type
} from '@angular/core';
import { AbstractLegend } from '@jeppesen-foreflight/jmkw-toolkit';
import { TwcTime } from '../twc-time';
import { TwcLayerType } from '../twc.mixin';
import { FlightLevelSelectComponent } from './components/flight-level-select/flight-level-select.component';
import { TimeSpanSelectComponent } from './components/time-span-select/time-span-select.component';

interface TwcLegendData {
  svgComponent?: Type<any>;
  pluginIDs?: string[];
  showTimeSpanSelectForSingleOption?: boolean;
}

@Component({
  selector: 'jmkw-twc-legend',
  templateUrl: './twc-legend.component.html',
  styleUrls: ['./twc-legend.component.scss'],
  standalone: true,
  imports: [CommonModule, TimeSpanSelectComponent, FlightLevelSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TwcLegendComponent extends AbstractLegend implements OnInit {
  private readonly TIMEZONE_FORMAT = 'dd/HH:mm';

  @Input() data?: TwcLegendData;
  layer?: TwcLayerType;

  // Track all layers to watch
  private layersToWatch: TwcLayerType[] = [];

  get timeMultiplier(): number {
    return this.layer?.type === 'web-tile' ? 1000 : 1;
  }

  get shouldShowTimeSpanSelect(): boolean {
    const timeMapSize = this.layer?.timeMap?.()?.size ?? 0;
    return (
      timeMapSize > 1 ||
      (timeMapSize === 1 && !!this.data?.showTimeSpanSelectForSingleOption)
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.plugin) return;

    this.layer = this.plugin.layer as TwcLayerType;
    this.collectLayersToWatch();
    this.setupMetadataWatches();
  }

  private collectLayersToWatch(): void {
    // Always add the main layer if it exists
    if (this.layer) {
      this.layersToWatch.push(this.layer);
    }

    // Add layers from related plugins if specified in data
    if (this.data?.pluginIDs?.length) {
      this.data.pluginIDs.forEach((pluginId) => {
        const relatedLayer =
          this.toolkitApiService.getLayerById<TwcLayerType>(pluginId);
        if (relatedLayer) {
          this.layersToWatch.push(relatedLayer);
        }
      });
    }
  }

  updateSelectedTime(selectedTime: string): void {
    this.layersToWatch.forEach((layer) => {
      if (layer) {
        layer.selectedTime = selectedTime;
      }
    });
  }

  updateSelectedFlightLevel(selectedAltitude: string): void {
    this.layersToWatch.forEach((layer) => {
      if (layer) {
        layer.selectedFlightLevel = selectedAltitude;
      }
    });
  }

  private setupMetadataWatches(): void {
    this.layersToWatch.forEach((layer) => {
      if (!layer?.metadata) return;

      const handle = this.setupWatch(
        () => layer.metadata?.['updatedAt'],
        (updatedAt: number) => this.handleMetadataUpdate(updatedAt)
      );

      if (handle) {
        this.watchHandles.add(handle);
      }
    });
  }

  handleMetadataUpdate(updatedAt: number): void {
    if (!this.plugin?.legend?.legendTitleBarInfo) return;

    const titleBarText = updatedAt
      ? this.formatUpdatedTime(updatedAt)
      : 'NO DATA AVAILABLE';

    this.plugin.legend.legendTitleBarInfo.set(titleBarText);
  }

  private formatUpdatedTime(updatedAt: number): string {
    const twcTime = new TwcTime(updatedAt);
    return `${twcTime.stringify(this.timeMultiplier, this.TIMEZONE_FORMAT)} UTC`;
  }
}
